import { useEffect, useState } from "react";
import { getSignedDocUrl, type ReviewApplication } from "@/lib/seller-review";
import { timeAgo } from "@/pages/admin/dashboard/types";

interface ApplicationCardProps {
  item: ReviewApplication;
  onApprove: (userId: string) => void;
  onReject: (userId: string) => void;
  onSuspend: (userId: string) => void;
}

const STATUS_META: Record<string, { label: string; badge: string }> = {
  submitted: { label: "Pendiente", badge: "bg-accent-100 text-accent-800" },
  under_review: { label: "En revisión", badge: "bg-secondary-100 text-secondary-700" },
  approved: { label: "Aprobado", badge: "bg-primary-100 text-primary-700" },
  rejected: { label: "Rechazado", badge: "bg-background-100 text-foreground-600" },
  suspended: { label: "Suspendido", badge: "bg-accent-100 text-accent-800" },
};

const SELLER_TYPE_LABEL: Record<string, string> = {
  particular: "Particular",
  empresa: "Empresa / Negocio",
};

function initials(name: string | null, email: string | null): string {
  return (name ?? email ?? "U").charAt(0).toUpperCase();
}

export default function ApplicationCard({
  item,
  onApprove,
  onReject,
  onSuspend,
}: ApplicationCardProps) {
  const app = item.application;
  const status = app.status;
  const meta = STATUS_META[status] ?? STATUS_META.submitted;

  const [expanded, setExpanded] = useState(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, string | null>>({});
  const [loadingUrls, setLoadingUrls] = useState(false);

  useEffect(() => {
    if (!expanded || item.docs.length === 0) return;
    let mounted = true;
    setLoadingUrls(true);
    (async () => {
      const result: Record<string, string | null> = {};
      for (const doc of item.docs) {
        result[doc.storage_path] = await getSignedDocUrl(doc.storage_path);
      }
      if (mounted) {
        setSignedUrls(result);
        setLoadingUrls(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [expanded, item.docs]);

  const locationText = [
    app.address,
    app.city,
    app.province,
    app.country,
  ]
    .filter(Boolean)
    .join(", ");

  const isPending = status === "submitted" || status === "under_review";

  return (
    <div className="overflow-hidden border-b border-background-200/60 last:border-0">
      {/* Fila resumen */}
      <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
          {initials(item.full_name, item.email)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-foreground-950">
              {item.full_name ?? "Vendedor"}
            </p>
            <span
              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.badge}`}
            >
              {meta.label}
            </span>
          </div>
          <p className="truncate text-xs text-foreground-500">{item.email}</p>
          <p className="mt-0.5 truncate text-xs text-foreground-400">
            {app.store_name || "Sin tienda"} · {app.primary_category || "Sin categoría"} ·{" "}
            {timeAgo(app.created_at)}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className={expanded ? "ri-arrow-up-s-line" : "ri-eye-line"}></i>
            {expanded ? "Ocultar" : "Detalles"}
          </button>

          {isPending && (
            <>
              <button
                type="button"
                onClick={() => onApprove(app.user_id)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-check-line"></i>
                Aprobar
              </button>
              <button
                type="button"
                onClick={() => onReject(app.user_id)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-100"
              >
                <i className="ri-close-line"></i>
                Rechazar
              </button>
            </>
          )}

          {status === "approved" && (
            <button
              type="button"
              onClick={() => onSuspend(app.user_id)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-100"
            >
              <i className="ri-pause-circle-line"></i>
              Suspender
            </button>
          )}

          {status === "rejected" && (
            <button
              type="button"
              onClick={() => onApprove(app.user_id)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-check-line"></i>
              Aprobar
            </button>
          )}

          {status === "suspended" && (
            <button
              type="button"
              onClick={() => onApprove(app.user_id)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              Reactivar
            </button>
          )}
        </div>
      </div>

      {/* Detalle expandido */}
      {expanded && (
        <div className="border-t border-background-200/60 bg-background-100/30 px-5 py-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* Info del negocio */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                Información del negocio
              </h4>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">Tienda</dt>
                  <dd className="text-right font-medium text-foreground-900">
                    {app.store_name || "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">Nombre comercial</dt>
                  <dd className="text-right font-medium text-foreground-900">
                    {app.business_name || "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">Tipo</dt>
                  <dd className="text-right font-medium text-foreground-900">
                    {SELLER_TYPE_LABEL[app.seller_type ?? ""] ?? "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">Categoría</dt>
                  <dd className="text-right font-medium text-foreground-900">
                    {app.primary_category || "—"}
                  </dd>
                </div>
                {app.secondary_categories &&
                  app.secondary_categories.length > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">Secundarias</dt>
                      <dd className="text-right font-medium text-foreground-900">
                        {app.secondary_categories.join(", ")}
                      </dd>
                    </div>
                  )}
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">Teléfono</dt>
                  <dd className="text-right font-medium text-foreground-900">
                    {app.seller_phone || "—"}
                  </dd>
                </div>
                {app.store_description && (
                  <div className="pt-1">
                    <dt className="text-foreground-500">Descripción</dt>
                    <dd className="mt-1 text-foreground-700">
                      {app.store_description}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Ubicación */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                Ubicación
              </h4>
              <p className="mt-3 text-sm text-foreground-700">
                {locationText || "Sin ubicación"}
              </p>
              {app.latitude != null && app.longitude != null && (
                <div className="mt-3 overflow-hidden rounded-md border border-background-200">
                  <iframe
                    title="Ubicación del vendedor"
                    src={`https://www.google.com/maps?q=${app.latitude},${app.longitude}&z=14&output=embed`}
                    className="h-40 w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              )}
              <div className="mt-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  Políticas
                </h4>
                <p className="mt-2 text-sm text-foreground-700">
                  {app.accepted_terms
                    ? `Aceptadas (v${app.terms_version ?? "—"})`
                    : "No aceptadas"}
                </p>
              </div>
            </div>

            {/* Documentos */}
            <div className="lg:col-span-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                Documentos de identidad
              </h4>
              {item.docs.length === 0 ? (
                <p className="mt-2 text-sm text-foreground-500">
                  Sin documentos cargados.
                </p>
              ) : loadingUrls ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-foreground-500">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Cargando documentos…
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-4">
                  {item.docs.map((doc) => (
                    <div key={doc.id} className="w-40">
                      <p className="mb-1.5 text-xs font-medium text-foreground-600">
                        {doc.document_type === "front"
                          ? "Cédula frontal"
                          : "Cédula posterior"}
                      </p>
                      {signedUrls[doc.storage_path] ? (
                        <a
                          href={signedUrls[doc.storage_path] ?? "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block cursor-pointer overflow-hidden rounded-md border border-background-200"
                        >
                          <img
                            src={signedUrls[doc.storage_path] ?? ""}
                            alt={
                              doc.document_type === "front"
                                ? "Cédula frontal"
                                : "Cédula posterior"
                            }
                            className="h-32 w-full object-cover object-top"
                          />
                        </a>
                      ) : (
                        <div className="flex h-32 w-full items-center justify-center rounded-md border border-background-200 bg-background-100 text-foreground-400">
                          <i className="ri-lock-line text-xl"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}